/**
 * LaTeX Preview Component
 * Preview and export LaTeX documents
 */

import React, { useState } from 'react';
import { useProcessingStore } from '../store/useProcessingStore';
import { Download, Copy, CheckCircle, FileText, Eye } from 'lucide-react';
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';

export const LaTeXPreview: React.FC = () => {
  const { result } = useProcessingStore();
  const [copied, setCopied] = useState(false);
  const [previewMode, setPreviewMode] = useState<'rendered' | 'source'>('rendered');

  if (!result || !result.latexDocument) {
    return null;
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(result.latexDocument);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([result.latexDocument], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `solution_${result.processId}.tex`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderDifficultyStars = (score: number = 3) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < score ? 'text-yellow-500' : 'text-gray-300'}>
          ★
        </span>
      );
    }
    return stars;
  };

  const renderLatexContent = () => {
    // Simple LaTeX to React conversion for preview
    // In production, consider using a more robust LaTeX parser
    return result.solutions.map((solution, index) => {
      const problem = result.interpretation.problems[index];

      return (
        <div key={solution.problemId} className="mb-8 pb-8 border-b border-gray-200 last:border-0">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">
              问题 {index + 1}
            </h3>
            <div className="flex flex-col items-end space-y-1">
              {problem?.subject && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {problem.subject}
                </span>
              )}
              {problem?.difficultyScore !== undefined && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">难度:</span>
                  <div className="text-lg">
                    {renderDifficultyStars(problem.difficultyScore)}
                  </div>
                  <span className="text-sm text-gray-500">({problem.difficultyScore}/5)</span>
                </div>
              )}
            </div>
          </div>

          {problem && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-700 font-medium">{problem.content}</p>
            </div>
          )}

          {solution.steps.map((step, stepIndex) => (
            <div key={stepIndex} className="mb-4 pl-4 border-l-2 border-blue-300">
              <h4 className="font-semibold text-gray-700 mb-2">
                步骤 {step.stepNumber}
              </h4>
              <p className="text-gray-600 mb-2">{step.description}</p>
              {step.formula && (
                <div className="my-2 overflow-x-auto">
                  <BlockMath>{step.formula}</BlockMath>
                </div>
              )}
              <p className="text-sm text-gray-500 italic">{step.explanation}</p>
            </div>
          ))}

          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-2">最终答案</h4>
            <p className="text-gray-700">{solution.finalAnswer}</p>
            {solution.explanation && (
              <p className="text-sm text-gray-600 mt-2">{solution.explanation}</p>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center space-x-2">
                <FileText className="w-6 h-6" />
                <span>LaTeX 文档</span>
              </h2>
              <p className="mt-1 text-green-100">
                预览和导出生成的LaTeX文档
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPreviewMode(prev => prev === 'rendered' ? 'source' : 'rendered')}
                className="px-4 py-2 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors flex items-center space-x-2"
              >
                <Eye className="w-4 h-4" />
                <span>{previewMode === 'rendered' ? '查看源码' : '查看渲染'}</span>
              </button>
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors flex items-center space-x-2"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? '已复制' : '复制'}</span>
              </button>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>下载</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {previewMode === 'rendered' ? (
            <div className="prose prose-lg max-w-none">
              {renderLatexContent()}
            </div>
          ) : (
            <pre className="bg-gray-900 text-green-400 p-6 rounded-lg overflow-x-auto font-mono text-sm max-h-[600px] overflow-y-auto">
              {result.latexDocument}
            </pre>
          )}
        </div>

        {/* Stats */}
        <div className="p-6 bg-gray-50 border-t grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">问题数量</p>
            <p className="text-2xl font-bold text-gray-800">{result.solutions.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">生成时间</p>
            <p className="text-2xl font-bold text-gray-800">
              {new Date(result.completedAt).toLocaleTimeString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">文档大小</p>
            <p className="text-2xl font-bold text-gray-800">
              {(result.latexDocument.length / 1024).toFixed(2)} KB
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
